import React, { useState } from 'react';
import Button from '../Button';

interface CopyButtonProps {
  textToCopy: string;
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'white' | 'link' | 'danger';
  fullWidth?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  // Feedback options
  showTextChange?: boolean;
  copiedText?: string;
  defaultText?: string;
  // Callbacks
  onCopySuccess?: () => void;
  onCopyError?: (error: Error) => void;
  // Other button props
  [key: string]: any;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  textToCopy,
  children,
  variant = 'primary',
  fullWidth = false,
  disabled = false,
  style = {},
  // Feedback options
  showTextChange = false,
  copiedText = 'Copied!',
  defaultText,
  // Callbacks
  onCopySuccess,
  onCopyError,
  // Other props
  ...props
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [displayText, setDisplayText] = useState(defaultText || children);

  const handleCopy = async () => {
    if (isCopied || disabled) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);

      // Change button text
      if (showTextChange && displayText) {
        setDisplayText(copiedText);
      }

      // Call success callback
      onCopySuccess?.();

      // Reset after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
        if (showTextChange && displayText) {
          setDisplayText(defaultText || children);
        }
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
      onCopyError?.(error as Error);
    }
  };

  return (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled || isCopied}
      onClick={handleCopy}
      style={style}
    >
      {isCopied ? 'Copied' : displayText}
    </Button>
  );
};

export default CopyButton;
